import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface Donation {
  id: string;
  user_id: string | null;
  amount: number;
  currency: string;
  transaction_id: string;
  donor_email?: string;
  donor_name?: string;
  message?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export function useDonation() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user's donation history
  const donationsQuery = useQuery({
    queryKey: ['donations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('donations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching donations:', error);
        throw new Error(`Error fetching donations: ${error.message}`);
      }

      return data as Donation[];
    },
    enabled: !!user?.id,
  });

  // Record donation mutation
  const recordDonation = useMutation({
    mutationFn: async (params: {
      transaction_id: string;
      amount: number;
      currency?: string;
      donor_email?: string;
      donor_name?: string;
      message?: string;
    }) => {
      const donationData: any = {
        transaction_id: params.transaction_id,
        amount: params.amount,
        currency: params.currency || 'USD',
        status: 'completed',
      };

      // Add user_id if logged in
      if (user?.id) {
        donationData.user_id = user.id;
      }

      // Add optional fields
      if (params.donor_email) donationData.donor_email = params.donor_email;
      if (params.donor_name) donationData.donor_name = params.donor_name;
      if (params.message) donationData.message = params.message;

      const { data, error } = await supabase.functions.invoke('process-donation', {
        body: donationData,
      });

      if (error) {
        console.error('Error recording donation:', error);
        // Extract error message from FunctionsHttpError
        let errorMessage = 'Failed to record donation';
        try {
          if (error.context?.body) {
            const bodyText = await new Response(error.context.body).text();
            const parsed = JSON.parse(bodyText);
            errorMessage = parsed.error || errorMessage;
          }
        } catch {}
        throw new Error(errorMessage);
      }

      return data as Donation;
    },
    onSuccess: (data) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      
      toast({
        title: "Thank you!",
        description: `Your donation of $${data.amount} has been recorded.`,
      });
    },
    onError: (error: Error) => {
      console.error('Donation error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to record donation",
        variant: "destructive",
      });
    },
  });

  return {
    donations: donationsQuery.data,
    isLoading: donationsQuery.isLoading,
    error: donationsQuery.error,
    recordDonation,
  };
}
